/**
 * Utils format data for channels Metrics table
 */

import type { DoubleEntryTableRows } from "components/DoubleEntryTable";
import type { FilterOption } from "components/shared/FiltersAutocomplete";
import { channelKindsI18n } from "containers/Channels";
import { computeGrowthIndex, formatNumber } from "helpers/utils";
import type { ChannelPeriodSalesFromAPI, ChannelSales, ChannelSalesData } from "store/analyticsStore";
import type { IDatesRange } from "types";

import type { DataType } from "../SalesComparisonChart/utils";
import { isCurrencyDataType } from "../SalesComparisonChart/utils";

export type ChannelsMetricsByKind = Record<number, any>;

const emptyPeriodValues = () => ({ N: null, N_1: null, growthIndex: null });

function upsertChannelMetric(
    channelData: ChannelSalesData,
    dataType: keyof ChannelSalesData,
    periodKey: "N" | "N_1",
    value: number | null | undefined,
): ChannelSalesData[keyof ChannelSalesData] {
    return {
        ...channelData[dataType],
        [periodKey]: value ?? null,
    };
}

export function parseChannelPeriodSales(rawData: ChannelPeriodSalesFromAPI): ChannelSales[] {
    const mergedChannelPeriodSales = (Object.keys(rawData) as Array<keyof ChannelPeriodSalesFromAPI>).reduce<
        ChannelSales[]
    >((acc, period) => {
        const salesData = rawData[period];

        salesData.map((salesData) => {
            const currentChannelData = acc.find((c) => c.id === salesData.channelId);

            const periodKey = period === "salesN" ? "N" : "N_1";

            const previousData = (currentChannelData?.data as ChannelSalesData | undefined) ?? {
                turnover: emptyPeriodValues(),
                quantities: emptyPeriodValues(),
                turnover_1: emptyPeriodValues(),
                turnover_2: emptyPeriodValues(),
            };

            const updatedChannelData: ChannelSales = {
                id: salesData.channelId,
                kind: salesData.channelKind,
                name: salesData.channelName,
                data: {
                    turnover: upsertChannelMetric(
                        previousData,
                        "turnover",
                        periodKey,
                        salesData.total_channel_id_turnover,
                    ),
                    quantities: upsertChannelMetric(
                        previousData,
                        "quantities",
                        periodKey,
                        salesData.total_channel_id_quantities,
                    ),
                    turnover_1: upsertChannelMetric(
                        previousData,
                        "turnover_1",
                        periodKey,
                        salesData.total_channel_id_turnover_1,
                    ),
                    turnover_2: upsertChannelMetric(
                        previousData,
                        "turnover_2",
                        periodKey,
                        salesData.total_channel_id_turnover_2,
                    ),
                },
            };

            if (currentChannelData) {
                acc = [...acc.filter((c) => c.id !== updatedChannelData.id), updatedChannelData];
            } else {
                acc.push(updatedChannelData);
            }
        });

        return acc;
    }, []);

    return mergedChannelPeriodSales.map((channelSales) => {
        const data = channelSales.data as ChannelSalesData;
        return {
            ...channelSales,
            data: {
                turnover: {
                    ...data.turnover,
                    growthIndex: computeGrowthIndex(data.turnover.N_1, data.turnover.N),
                },
                quantities: {
                    ...data.quantities,
                    growthIndex: computeGrowthIndex(data.quantities.N_1, data.quantities.N),
                },
                turnover_1: {
                    ...data.turnover_1,
                    growthIndex: computeGrowthIndex(data.turnover_1.N_1, data.turnover_1.N),
                },
                turnover_2: {
                    ...data.turnover_2,
                    growthIndex: computeGrowthIndex(data.turnover_2.N_1, data.turnover_2.N),
                },
            },
        };
    });
}

export const channelsRowsTransform = (channelsMetricsByKind: ChannelsMetricsByKind | undefined, dataType: DataType) => {
    if (!channelsMetricsByKind) return [];
    const totalChannels = { label: "Total", values: [0, 0, 0] } as DoubleEntryTableRows; // [N, N-1, growthIndex]
    const isCurrency = isCurrencyDataType(dataType);

    const rows = Object.entries(channelsMetricsByKind).map(([kind, channels]) => {
        const label = channelKindsI18n()[kind];

        /** Format total channel data for a channel kind */
        const { N, N_1, growthIndex, collapseRows } = channels.reduce(
            (acc, ch, i) => {
                const isLastIndex = i === channels.length - 1;
                acc.N += ch.data[dataType].N;
                acc.N_1 += ch.data[dataType].N_1;

                // Compute total channels
                totalChannels.values[0] += ch.data[dataType].N;
                totalChannels.values[1] += ch.data[dataType].N_1;

                // Values for a channel (inside the collapsed row)
                const { N: chN, N_1: chN_1, growthIndex: chGrowthIndex } = ch.data[dataType];
                const channelDetailsRow = {
                    label: ch.name,
                    values: [
                        formatNumber(chN, { isCurrency }),
                        formatNumber(chN_1, { isCurrency }),
                        chGrowthIndex ? formatNumber(chGrowthIndex, { noDigit: true }) : "-",
                    ], // [N, N-1, growthIndex]
                };
                acc.collapseRows.push(channelDetailsRow);

                // Compute growthIndew once calculate N and N_1 values
                if (isLastIndex) {
                    acc.growthIndex = computeGrowthIndex(acc.N_1, acc.N);
                    const totalGrowthIndex = computeGrowthIndex(totalChannels.values[1], totalChannels.values[0]);
                    totalChannels.values[2] = totalGrowthIndex
                        ? formatNumber(totalGrowthIndex, { noDigit: true })
                        : "-";
                }
                return acc;
            },
            { N: 0, N_1: 0, growthIndex: 0, collapseRows: [] as DoubleEntryTableRows[] },
        );

        // Total values for group of channels (kind) row --> [N, N-1, growthIndex]
        const values = [
            formatNumber(N, { isCurrency }),
            formatNumber(N_1, { isCurrency }),
            growthIndex ? formatNumber(growthIndex, { noDigit: true }) : "-",
        ];

        return {
            label,
            values,
            collapseRows,
        } as DoubleEntryTableRows;
    });

    /** Format and push total row */
    totalChannels.values[0] = formatNumber(totalChannels.values[0] as number, { isCurrency });
    totalChannels.values[1] = formatNumber(totalChannels.values[1] as number, { isCurrency });
    rows.push(totalChannels);

    return rows;
};

/**
 * Chosen options type for Channels Metrics filters
 */
export type ChosenOptions = {
    chosenTitles: FilterOption[];
    chosenDates: IDatesRange;
    dataType: DataType;
};
